import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Icon,
    Input,
    Dropdown,
    ListItem,
    ListHeader,
    List,
    Button,
    Select,
    Form,
    FormGroup,
    FormField,
    Message,
    TextArea,
    Popup,
    Checkbox
} from 'semantic-ui-react'
import { ActionButton, LoadingWrapper } from "../common";
import { useNavigate } from 'react-router-dom';
import {canEditAll, canEditAssets, getCSRFToken, fetchWithAuth, canEditLimited} from '../auth';
import { searchAssets } from '../common/apiClient';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { State } from 'country-state-city'
import "../../css/Assets.css"

const US_STATE_ABBREVIATION_OPTIONS = State.getStatesOfCountry('US').map((state) => ({
    key: state.isoCode,
    value: state.isoCode,
    text: state.isoCode,
    description: state.name
}))

export const AssetDetails = props => {

    // this is just being created now
    const [asset, setAsset] = useState(null);
    const [teams, setTeams] = useState([])
    const [associatedAssets, setAssociatedAssets] = useState([])
    const [selectedAssets, setSelectedAssets] = useState([])
    const [dropdownOptions, setDropdownOptions] = useState([])
    const [dropdownLoading, setDropdownLoading] = useState(false)
    const [members, setMembers] = useState([])
    const [neededFields, setNeededFields] = useState({})
    const [deviceTypes, setDeviceTypes] = useState([])
    const [deviceSubTypes, setDeviceSubTypes] = useState([])
    const [deviceTypeOptionsWithDividers, setDeviceTypeOptionsWithDividers] = useState([])
    const [selectedType, setSelectedType] = useState()
    const [succeeded, setSucceeded] = useState(false)
    const [inputErrors, setInputErrors] = useState({})
    const [genericError, setGenericError] = useState("")
    const [assetLoaded, setAssetLoaded] = useState(false)
    const [typesLoaded, setTypesLoaded] = useState(false)
    const [teamsLoaded, setTeamsLoaded] = useState(false)
    const [membersLoaded, setMembersLoaded] = useState(false)
    const [showAll, setShowAll] = useState(true)
    const [explainChecklist, setExplainChecklist] = useState(props.explainChecklist)
    const { id } = useParams();
    const isNew = id === 'newasset'
    const navigate = useNavigate();


    const notRequired = {
        'assignedMember': true,
        'associatedAsset': true,
        'mileage': true,
        'notes': true,
        'operationalHours': true,
        'requiredForReadiness': true
    }

    const ValidatedField = props => {
        const { fieldName, label, onChange, alwaysPresent, numerical, ...rest } = props
        if (!showAll && !neededFields[fieldName] && !props.alwaysPresent) {
            return <></>
        }

        const hasErrored = inputErrors[fieldName]

        return <FormField
            style={{ marginBottom: !hasErrored ? "1rem" : "0", minWidth: "0" }}
            error={
                hasErrored && (
                    { content: inputErrors[fieldName] }
                )
            }
            label={label}
            value={asset[fieldName] ? asset[fieldName] : ""}
            name={fieldName}
            key={fieldName}
            onChange={onChange ? onChange : handleChange}
            required={true && neededFields[fieldName] && !notRequired[fieldName]}
            readOnly={fieldName === 'notes' ? (!canEditLimited() && !canEditAssets()) : !canEditAssets()}
            onKeyPress={(event) => {
                if (numerical && !/[0-9]/.test(event.key)) {
                    event.preventDefault();
                }
            }}
            {...rest}
        />
    }

    const validateForm = () => {
        let isValid = true
        const newInputErrors = {}
        if (explainChecklist) {
            setGenericError('Please explain why the checklist was not verified.')
            isValid = false;
        }
        if ((neededFields['operationalHours'] && neededFields['mileage']) && (!asset['operationalHours'] && !asset['mileage'])) {
            setGenericError('Operational hours or mileage must be filled in.')
            isValid = false
        }
        const alwaysNeeded = {
            'status': true,
            'iccName': true,
            'deviceSubType': true
        }
        for (const field in alwaysNeeded) {
            if ((!asset[field] || !(field in asset))) {
                newInputErrors[field] = "This field is required."
            }
        }
        for (const field in neededFields) {
            if (notRequired[field]) {
                continue;
            }
            if (neededFields[field] && (!asset[field] || !(field in asset))) {
                newInputErrors[field] = "This field is required."
            }
        }
        if (Object.keys(newInputErrors).length !== 0) {
            setInputErrors(newInputErrors)
            isValid = false
        }
        return isValid
    }

    //////////// helper functions to convert asset format /////////////////
    const normalizeAsset = (asset) => {
        asset.deviceType = asset.deviceSubType.deviceType.id
        asset.deviceSubType = asset.deviceSubType.id
        asset.associatedTeam = asset.associatedTeam?.id
        asset.associatedAssets = (asset.associatedAssets || []).map((as) => ({
            id: as.id,
            name: as.name
        }))
        asset.assignedMember = asset.assignedMember?.userId
        asset.registrationExpiration = asset.registrationExpiration ? new Date(asset.registrationExpiration + "T12:00:00Z") : null
        asset.dotInspectionDate = asset.dotInspectionDate ? new Date(asset.dotInspectionDate + "T12:00:00Z") : null
        asset.dotRegistrationDate = asset.dotRegistrationDate ? new Date(asset.dotRegistrationDate + "T12:00:00Z") : null
        asset.dotExpirationDate = asset.dotExpirationDate ? new Date(asset.dotExpirationDate + "T12:00:00Z") : null
        asset.status = asset.status ? asset.status : null
        asset.requiredForReadiness = asset.requiredForReadiness || false
        if (asset.stateOfRegistration) {
            const matchedState = State.getStatesOfCountry('US').find((state) => (
                state.isoCode === asset.stateOfRegistration
                || state.name === asset.stateOfRegistration
            ))
            if (matchedState) {
                asset.stateOfRegistration = matchedState.isoCode
            }
        }
        return asset
    }

    const denormalizeAsset = (asset) => {
        asset.deviceSubType = {
            id: asset.deviceSubType
        }
        if (asset.associatedTeam) {
            asset.associatedTeam = {
                id: asset.associatedTeam
            }
        } else if ('associatedTeam' in asset) {
            delete asset.associatedTeam
        }
        asset.associatedAssets = (asset.associatedAssets || []).map((as) => ({
            id: as?.id ?? as
        }))
        if (asset.assignedMember) {
            asset.assignedMember = {
                userId: asset.assignedMember
            }
        }
        else if ('assignedMember' in asset) {
            delete asset.assignedMember
        }
        if (neededFields['registrationExpiration'] || asset.registrationExpiration !== null) {
            asset.registrationExpiration = asset.registrationExpiration?.toISOString().substring(0, 10)
        } else if ('registrationExpiration' in asset) {
            delete asset.registrationExpiration
        }
        if (neededFields['dotInspectionDate'] || asset.dotInspectionDate!=null) {
            asset.dotInspectionDate = asset.dotInspectionDate?.toISOString().substring(0, 10)
        } else if ('dotInspectionDate' in asset) {
            delete asset.dotInspectionDate
        }
        if (neededFields['dotRegistrationDate'] || asset.dotRegistrationDate!=null) {
            asset.dotRegistrationDate = asset.dotRegistrationDate?.toISOString().substring(0, 10)
        } else if ('dotRegistrationDate' in asset) {
            delete asset.dotRegistrationDate
        }
        if (neededFields['dotExpirationDate'] || asset.dotExpirationDate!=null) {
            asset.dotExpirationDate = asset.dotExpirationDate?.toISOString().substring(0, 10)
        } else if ('dotExpirationDate' in asset) {
            delete asset.dotExpirationDate
        }
        if (asset.mileage === "") {
            delete asset['mileage']
        }
        if (asset.operationalHours === "") {
            delete asset['operationalHours']
        }
        if (asset.requiredForReadiness !== undefined) {
            asset.requiredForReadiness = Boolean(asset.requiredForReadiness)
        }
        return asset
    }
    /////////////////////////////////////////////////////////////////////////

    function submitForm() {
        setSucceeded(false)
        setInputErrors({})
        setGenericError("")
        async function updateAsset(denormalizedAsset) {
            setGenericError(false)
            setAssetLoaded(false)
            const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `assets/${id}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCSRFToken()
                },
                credentials: 'include',
                body: JSON.stringify(denormalizedAsset)
            })
            setAssetLoaded(true)
            try {
                const respJson = await resp.json();
                if (resp.ok) {
                    const normalizedAsset = normalizeAsset(respJson)
                    setAsset(normalizedAsset)
                    setSelectedAssets([])
                    setAssociatedAssets(normalizedAsset.associatedAssets)
                    setSucceeded(true)
                } else {
                    setInputErrors(respJson)
                }
            } catch {
                setGenericError("An unkown error occurred.")
            }
        }

        async function createAsset(denormalizedAsset) {
            setAssetLoaded(false)
            const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'assets/', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    'X-CSRFToken': getCSRFToken()
                },
                credentials: 'include',
                body: JSON.stringify(denormalizedAsset)
            })
            const respJson = await resp.json();
            setAssetLoaded(true)
            if (resp.ok) {
                setSucceeded(true)
                navigate('/assets/' + respJson.id + "/view")
            } else {
                setInputErrors(respJson)
            }
        }

        asset.associatedAssets = [...new Set([
            ...associatedAssets.map((as) => as.id),
            ...selectedAssets
        ])]

        window.scrollTo({ top: 0, behavior: "smooth" })

        let denormalizedAsset = denormalizeAsset(structuredClone(asset))
        if (!validateForm()) {
            return
        }

        if (isNew) {
            createAsset(denormalizedAsset)
        } else {
            updateAsset(denormalizedAsset)
        }
        setSucceeded(false)
    }

    function getTeams() {
        async function getTeamsGet() {
            setTeamsLoaded(false)
            const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'teams/', {
                headers: { 'X-CSRFToken': getCSRFToken() },
                credentials: 'include'
            });
            const respJson = await resp.json()
            setTeams(respJson)
            setTeamsLoaded(true)
        }
        getTeamsGet()
    }

    const searchAssetsForDropdown = (query) => {
        async function doSearch() {
            setDropdownLoading(true)
            const results = await searchAssets(query)
            setDropdownOptions(prev => {
                const pinned = prev.filter(
                    a => selectedAssets.includes(a.id) && !results.some(r => r.id === a.id)
                )
                return pinned.length ? [...pinned, ...results] : results
            })
            setDropdownLoading(false)
        }
        doSearch()
    }

    function getDeviceTypes() {
        async function getDeviceTypesGet() {
            setTypesLoaded(false)
            const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + "devicetypes/", {
                headers: { 'X-CSRFToken': getCSRFToken() },
                credentials: 'include'
            })
            const respJson = await resp.json()
            setDeviceTypes(respJson)

            const resp2 = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + "devicesubtypes/", {
                headers: { 'X-CSRFToken': getCSRFToken() },
                credentials: 'include'
            })
            const respJson2 = await resp2.json()
            setDeviceSubTypes(respJson2)
            setTypesLoaded(true)
        }
        // need to get the main device types, then the sub device types
        // then we figure out which sub device type we have chosen,
        // get the fields for that, and set those as the required fields
        getDeviceTypesGet()
    }

    const getMembers = () => {
        async function getMembersGet() {
            setMembersLoaded(false)
            const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + "members/", {
                headers: { 'X-CSRFToken': getCSRFToken() },
                credentials: 'include'
            })
            const respJson = await resp.json()
            setMembers(respJson)
            setMembersLoaded(true)
        }
        getMembersGet()
    }

    const fetchAssetDetails = async () => {
        setAssetLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `assets/${id}/`, {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        const data = await response.json();

        const normalizedAsset = normalizeAsset(data)
        setAsset(normalizedAsset)
        setAssociatedAssets(normalizedAsset.associatedAssets)
        setAssetLoaded(true)
    };

    useEffect(() => {
        // set the selected type only when we have loaded the
        // device types AND the asset. otherwise we have race
        // conditions
        if (asset && deviceSubTypes.length && deviceTypes.length) {
            setSelectedType(asset.deviceSubType)
        }
    }, [asset, deviceSubTypes])

    useEffect(() => {
        setAsset({
            status: 'functional',
            requiredForReadiness: false
        })
        if (!isNew) {
            fetchAssetDetails();
        }
        getTeams()
        searchAssetsForDropdown('')
        getDeviceTypes()
        getMembers()
    }, [id]);

    useEffect(() => {
        // if the selected type has changed AND we have an asset
        // we want to recompute the fields we want to show

        // selected type will be the subtype. first find out associated type
        if (asset) {
            const newNeededFields = {}
            for (const type of deviceSubTypes) {
                if (type.id === selectedType) {
                    for (const fieldName of type.frontendFields) {
                        newNeededFields[fieldName] = true
                    }
                }
            }
            setNeededFields(newNeededFields)
        }
    }, [selectedType])

    useEffect(() => {
        // build our options
        if (deviceTypes.length && deviceSubTypes.length) {
            deviceTypes.sort((a, b) => a.name.localeCompare(b.name))
            deviceSubTypes.sort((a, b) => a.name.localeCompare(b.name))
            const options = []
            for (const deviceType of deviceTypes) {
                options.push({
                    key: deviceType.id + "header",
                    value: deviceType.id + "header",
                    text: deviceType.name,
                    content: <Dropdown.Item>{deviceType.name + "s"}</Dropdown.Item >,
                    disabled: true,
                    style: { backgroundColor: "lightgray", fontWeight: "bold" }
                })
                for (const deviceSubType of deviceSubTypes) {
                    if (deviceSubType.deviceType === deviceType.id) {
                        options.push({
                            key: deviceSubType.id,
                            value: deviceSubType.id,
                            text: deviceSubType.name + "-" + deviceSubType.assetDeviceType.name,
                            content: <Dropdown.Item style={{ paddingLeft: "0.5rem" }}>{deviceSubType.name}</Dropdown.Item>
                        })
                    }
                }
            }

            setDeviceTypeOptionsWithDividers(options)
        }
    }, [deviceTypes, deviceSubTypes])

    useEffect(() => {
        if(explainChecklist) {
            setGenericError("Please explain why the checklist was not verified.")
        } else {
            setGenericError("")
        }
    }, [explainChecklist])

    function handleChange(e, { name, value }) {
        const newAsset = Object.assign({}, asset)
        newAsset[name] = value
        inputErrors[name] = false

        // allow elevated associates to edit this field only
        if (name === 'notes') {
            if (canEditAll() || canEditLimited()) {
                setExplainChecklist(false)
                setAsset(newAsset)
            } else {
                return
            }
        }

        if (!canEditAssets()) {
            return
        }

        if (name === 'deviceSubType') {
            setSelectedType(value)
        }
        if (name === 'notes') {
            setExplainChecklist(false)
        }

        setAsset(newAsset)
    }

    function handleRemoveAsset(assetId) {
        setAssociatedAssets(associatedAssets.filter((aa) => aa.id !== assetId))
    }

    if (!asset) {
        return (<div>Loading...</div>)
    }

    return (
        <>
            <LoadingWrapper text="Loading asset" loading={isNew ? false : !assetLoaded}>
                <div align="center" style={{ paddingBottom: "2rem" }}>
                    {genericError && <Message style={{ width: "75%" }} negative>{genericError}</Message>}
                    {succeeded && <Message style={{ width: "75%" }} positive>Saved successfully</Message>}
                    <div id="asset-form-wrapper">
                        <Form>
                            <FormGroup>
                                {ValidatedField(
                                    {
                                        label: 'ICC Name',
                                        width: 8,
                                        fieldName: 'iccName',
                                        control: Input,
                                        alwaysPresent: true
                                    }
                                )}
                                {ValidatedField(
                                    {
                                        label: 'Fleet Name',
                                        width: 8,
                                        fieldName: 'fleetName',
                                        control: Input,
                                        alwaysPresent: true
                                    }
                                )}
                            </FormGroup>
                            <FormGroup>
                                {ValidatedField(
                                    {
                                        label: 'Type',
                                        width: 6,
                                        fieldName: 'deviceSubType',
                                        control: Select,
                                        alwaysPresent: true,
                                        options: deviceTypeOptionsWithDividers,
                                        search: true,
                                        loading: !typesLoaded
                                    }
                                )}
                                {ValidatedField(
                                    {
                                        label:
                                            (<label>
                                                Team {asset.associatedTeam ?
                                                    <Link
                                                        style={{ paddingLeft: "0.3rem" }}
                                                        to={`/teams/${asset.associatedTeam}`}>
                                                        (View team)
                                                    </Link> : ""}
                                                {!isNew && <span style={{ paddingLeft: "0.5rem", color: "grey", fontWeight: "normal", fontSize: "0.85em" }}>— use the <strong>Asset Transfer</strong> tab to change</span>}
                                            </label>),
                                        width: 5,
                                        fieldName: 'associatedTeam',
                                        control: Select,
                                        clearable: true,
                                        alwaysPresent: true,
                                        search: true,
                                        disabled: !isNew,
                                        options: teams.map((team) => {
                                            return {
                                                key: team.id,
                                                value: team.id,
                                                text: team.name
                                            }
                                        }),
                                        loading: !teamsLoaded
                                    }
                                )}
                                {ValidatedField(
                                    {
                                        label:
                                            (<label>
                                                Member {asset.assignedMember ?
                                                    <Link
                                                        style={{ paddingLeft: "0.3rem" }}
                                                        to={`/members/${asset.assignedMember}`}>
                                                        (View member)
                                                    </Link> : ""}
                                            </label>),
                                        width: 5,
                                        fieldName: 'assignedMember',
                                        control: Select,
                                        clearable: true,
                                        alwaysPresent: true,
                                        search: true,
                                        options: members.map((member) => {
                                            return {
                                                key: member.userId,
                                                value: member.userId,
                                                text: member.name
                                            }
                                        }),
                                        loading: !membersLoaded
                                    }
                                )}
                            </FormGroup>
                            <div className='divider-bottom' />
                            {(neededFields.locatorSerialNumber ||
                                neededFields.screenSerialNumber ||
                                neededFields.beaconSerialNumber ||
                                neededFields.trailerSerialNumber ||
                                neededFields.vin ||
                                showAll) && <>
                                    <FormGroup>
                                        {ValidatedField({
                                            label: 'Vin',
                                            width: 5,
                                            fieldName: 'vin',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'Beacon Serial Number',
                                            width: 5,
                                            fieldName: 'beaconSerialNumber',
                                            control: Input
                                        })}
                                    </FormGroup>
                                    <FormGroup>
                                        {ValidatedField({
                                            label: 'Locator Serial Number',
                                            width: 5,
                                            fieldName: 'locatorSerialNumber',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'Screen Serial Number',
                                            width: 5,
                                            fieldName: 'screenSerialNumber',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'Trailer Serial Number',
                                            width: 5,
                                            fieldName: 'trailerSerialNumber',
                                            control: Input
                                        })}
                                    </FormGroup>
                                    <div className='divider-bottom' /></>}
                            {(neededFields.make || neededFields.model ||
                                neededFields.year || showAll) && <>
                                    <FormGroup>
                                        {ValidatedField({
                                            label: 'Make',
                                            width: 4,
                                            fieldName: 'make',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'Model',
                                            width: 4,
                                            fieldName: 'model',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'Year',
                                            width: 4,
                                            fieldName: 'year',
                                            control: Input,
                                            numerical: true
                                        })}
                                    </FormGroup>
                                    <div className='divider-bottom' /></>}
                            {(neededFields.status ||
                                neededFields.model ||
                                neededFields.make ||
                                neededFields.tSite ||
                                neededFields.stateOfRegistation || showAll) && <>
                                    <FormGroup>
                                        {ValidatedField({
                                            label: 'Status',
                                            width: 4,
                                            fieldName: 'status',
                                            control: Select,
                                            options: [
                                                {
                                                    key: 'functional',
                                                    value: 'functional',
                                                    text: 'Functional'
                                                },
                                                {
                                                    key: 'maintenance',
                                                    value: 'maintenance',
                                                    text: 'Maintenance'
                                                },
                                                {
                                                    key: 'outOfService',
                                                    value: 'outOfService',
                                                    text: 'Out of Service'
                                                },
                                                {
                                                    key: 'warranty',
                                                    value: 'warranty',
                                                    text: 'Warranty/Recall'
                                                },
                                            ],
                                            alwaysPresent: true,
                                            search: true,
                                            className: "icct-disabled-selection"
                                        })}
                                        <FormField width={4}>
                                            <label htmlFor="requiredForReadinessToggle">Required for Readiness</label>
                                            <Checkbox
                                                id="requiredForReadinessToggle"
                                                toggle
                                                checked={asset.requiredForReadiness || false}
                                                onChange={(e, data) => handleChange(e, { name: 'requiredForReadiness', value: data.checked })}
                                                disabled={!canEditAssets()}
                                            />
                                        </FormField>

                                        {ValidatedField({
                                            label: 'Serial Number',
                                            width: 4,
                                            fieldName: 'serialNumber',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'T-Site',
                                            width: 4,
                                            fieldName: 'tSite',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'State of Registration',
                                            width: 4,
                                            fieldName: 'stateOfRegistration',
                                            control: Select,
                                            clearable: true,
                                            search: true,
                                            options: US_STATE_ABBREVIATION_OPTIONS
                                        })}
                                    </FormGroup>
                                    <div className='divider-bottom' /></>}
                            {(neededFields.registrationExpiration ||
                                neededFields.dotInspectionDate ||
                                neededFields.dotRegistrationDate ||
                                neededFields.dotExpirationDate ||
                                neededFields.plateNumber ||
                                showAll) && <>
                                    <FormGroup>
                                        {ValidatedField({
                                            label: 'Registration Expiration',
                                            fieldName: 'registrationExpiration',
                                            width: 6,
                                            control: () => {
                                                return <DatePicker
                                                    customInput={<Input icon='calendar' />}
                                                    value={asset.registrationExpiration?.toISOString().substring(0, 10)}
                                                    onChange={(value) => {
                                                        handleChange('', { name: 'registrationExpiration', value: value })
                                                    }}
                                                />
                                            },
                                            selected: asset.registrationExpiration
                                        })}
                                        {ValidatedField({
                                            label: 'Plate Number',
                                            fieldName: 'plateNumber',
                                            width: 12,
                                            control: Input
                                        })}
                                    </FormGroup>
                                    <FormGroup>
                                        {ValidatedField({
                                            label: 'DOT Inspection Date',
                                            fieldName: 'dotInspectionDate',
                                            width: 6,
                                            control: () => {
                                                return <DatePicker
                                                    customInput={<Input icon='calendar' />}
                                                    value={asset.dotInspectionDate?.toISOString().substring(0, 10)}
                                                    onChange={(value) => {
                                                        handleChange('', { name: 'dotInspectionDate', value: value })
                                                    }}
                                                />
                                            },
                                            selected: asset.dotInspectionDate
                                        })}
                                        {ValidatedField({
                                            label: 'DOT Registration Date',
                                            fieldName: 'dotRegistrationDate',
                                            width: 5,
                                            control: () => {
                                                return <DatePicker
                                                    customInput={<Input icon='calendar' />}
                                                    value={asset.dotRegistrationDate?.toISOString().substring(0, 10)}
                                                    onChange={(value) => {
                                                        handleChange('', { name: 'dotRegistrationDate', value: value })
                                                    }}
                                                />
                                            },
                                            selected: asset.dotRegistrationDate
                                        })}
                                        {ValidatedField({
                                            label: 'DOT Expiration Date',
                                            fieldName: 'dotExpirationDate',
                                            width: 5,
                                            control: () => {
                                                return <DatePicker
                                                    customInput={<Input icon='calendar' />}
                                                    value={asset.dotExpirationDate?.toISOString().substring(0, 10)}
                                                    onChange={(value) => {
                                                        handleChange('', { name: 'dotExpirationDate', value: value })
                                                    }}
                                                />
                                            },
                                            selected: asset.dotExpirationDate
                                        })}
                                    </FormGroup>
                                    <div className='divider-bottom' /></>}
                            {(neededFields.mileage ||
                                neededFields.operationalHours ||
                                neededFields.unitNumber ||
                                showAll) && <>
                                    <FormGroup>
                                        {ValidatedField({
                                            label: 'Mileage',
                                            width: 5,
                                            fieldName: 'mileage',
                                            control: Input,
                                            numerical: true
                                        })}
                                        {ValidatedField({
                                            label: 'Operational Hours',
                                            width: 5,
                                            fieldName: 'operationalHours',
                                            control: Input,
                                            numerical: true
                                        })}
                                        {ValidatedField({
                                            label: 'Unit Number',
                                            width: 5,
                                            fieldName: 'unitNumber',
                                            control: Input,
                                            numerical: true
                                        })}
                                    </FormGroup>
                                    <div className='divider-bottom' /></>}
                            {(neededFields.splicerSerialNumber ||
                                neededFields.cleaverSerialNumber ||
                                neededFields.exfoOtdrSerialNumber ||
                                neededFields.exfoOx1SerialNumber ||
                                neededFields.exfoNineSerialNumber ||
                                neededFields.exfoScopeSerialNumber ||
                                neededFields.aflFiberIdSerialNumber ||
                                neededFields.exfoThreecSerialNumber || showAll) && <>
                                    <FormGroup>
                                        {ValidatedField({
                                            label: 'Splicer Serial Number',
                                            width: 4,
                                            fieldName: 'splicerSerialNumber',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'Cleaver Serial Number',
                                            width: 4,
                                            fieldName: 'cleaverSerialNumber',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'EXFO OTDR Serial Number',
                                            width: 4,
                                            fieldName: 'exfoOtdrSerialNumber',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'EXFO OX1 Serial Number',
                                            width: 4,
                                            fieldName: 'exfoOx1SerialNumber',
                                            control: Input
                                        })}
                                    </FormGroup>
                                    <FormGroup>
                                        {ValidatedField({
                                            label: 'EXFO 945 Serial Number',
                                            width: 4,
                                            fieldName: 'exfoNineSerialNumber',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'EXFO Scope Serial Number',
                                            width: 4,
                                            fieldName: 'exfoScopeSerialNumber',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'AFL Fiber ID Serial Number',
                                            width: 4,
                                            fieldName: 'aflFiberIdSerialNumber',
                                            control: Input
                                        })}
                                        {ValidatedField({
                                            label: 'EXFO 352C Serial Number',
                                            width: 4,
                                            fieldName: 'exfoThreecSerialNumber',
                                            control: Input
                                        })}
                                    </FormGroup>
                                    <div className='divider-bottom' /></>}
                            <FormField
                                control={TextArea}
                                label='Notes'
                                value={asset.notes ? asset.notes : ""}
                                name='notes'
                                onChange={handleChange}
                                error={explainChecklist && "Please explain why the checklist was not verified."}
                                alwaysPresent={true}
                            />
                            {(neededFields.notes || showAll) && <div className='divider-bottom' />}
                            <FormGroup>
                                {associatedAssets.length ? <FormField
                                    control={List}
                                    label='Associated Assets'
                                    relaxed className="list"
                                    style={{ marginTop: "0", marginBottom: "1rem" }}
                                    items={associatedAssets.map(associatedAsset => {
                                        const assetName = associatedAsset.name
                                        const assetId = associatedAsset.id
                                        return (
                                            <div className='list-item' key={assetId}>
                                                <ListItem>
                                                    <ListHeader style={{ fontWeight: "bold" }} as={Link} to={"/assets/" + assetId}>{assetName}</ListHeader>
                                                </ListItem>
                                                {canEditAssets() && (<Button onClick={() => { handleRemoveAsset(assetId) }} style={{ width: "fit-content", height: "fit-content", padding: "0.5rem", margin: "0" }}>
                                                    <Icon style={{ margin: "0" }} color='red' link name='close' />
                                                </Button>)}
                                            </div>)
                                    })}
                                    width={6}
                                /> : <></>}
                                {canEditAssets() && <FormField
                                    control={Dropdown}
                                    label='Select Associated Assets'
                                    fluid multiple search selection
                                    placeholder="Associated Assets"
                                    value={selectedAssets}
                                    onChange={(e, data) => setSelectedAssets(data.value)}
                                    options={
                                        dropdownOptions
                                            .filter(a => !associatedAssets.some(aa => aa.id === a.id) && a.id !== asset.id)
                                            .map(a => ({ key: a.id, value: a.id, text: a.iccName }))
                                    }
                                    onSearchChange={(e, { searchQuery }) => searchAssetsForDropdown(searchQuery)}
                                    loading={dropdownLoading}
                                    filterOptions={(options) => options}
                                    width={8}
                                />}
                            </FormGroup>
                        </Form>

                    </div>
                    {canEditAssets() && <><ActionButton submitAction={submitForm}
                                                     resetHide={false}
                                                     resetText={'Cancel'}
                                                     showConfirmation={true}
                                                     resetAction={() => { navigate('/assets/') }}
                                                     disabled={!canEditAssets()}
                    />
                        <Popup
                            trigger={<Button style={{ margin: "0.5rem" }}><Icon className="options"></Icon>Options</Button>}
                            header=''
                            content={() => {
                                return <div style={{ display: "flex", flexDirection: "column" }}>
                                    <Button style={{ margin: "0.5rem" }} onClick={() => setShowAll(!showAll)}>
                                        {showAll ? "Hide optional fields" : "Show optional fields"}
                                    </Button>
                                </div>
                            }}
                            on='click'
                        />
                    </>}
                </div>
            </LoadingWrapper>
        </>
    )

};
